import React from 'react';
import PropTypes from 'prop-types';
import ExcelJS from 'exceljs';

export default function ExportWorklogsExcel({
  logs = [],
  filename,
  getUserName,
  getHours,
  getAmount,
  formatDate,
  render,
  buttonProps = {},
}) {
  const handleExport = React.useCallback(() => {
    if (!logs || logs.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    try {
      // Exportar Excel completo con múltiples hojas
      exportComplete(logs, { filename, getUserName, getHours, getAmount, formatDate });
    } catch (error) {
      console.error('Error al exportar:', error);
      alert('Error al generar el archivo Excel');
    }
  }, [logs, filename, getUserName, getHours, getAmount, formatDate]);

  // Renderizado personalizado
  if (typeof render === 'function') {
    return render(handleExport);
  }

  // Renderizado por defecto
  return (
    <button className="btn" onClick={handleExport} {...buttonProps}>
      Exportar a Excel
    </button>
  );
}

ExportWorklogsExcel.propTypes = {
  logs: PropTypes.array.isRequired,
  filename: PropTypes.string,
  getUserName: PropTypes.func,
  getHours: PropTypes.func,
  getAmount: PropTypes.func,
  formatDate: PropTypes.func,
  render: PropTypes.func,
  buttonProps: PropTypes.object,
};

// ==================== FUNCIONES HELPER ====================

/**
 * Función auxiliar para descargar el workbook
 */
async function saveWorkbook(wb, filename) {
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${filename}.xlsx`;
  anchor.click();
  window.URL.revokeObjectURL(url);
}

/**
 * Función genérica para agregar datos a una hoja
 */
function addSheetData(ws, data, totalColumns = []) {
  if (!data || data.length === 0) return;

  const headers = Object.keys(data[0]);

  // Definir columnas con clave y encabezado
  ws.columns = headers.map(header => ({
    header: header,
    key: header,
    // Ancho inicial, se ajustará después
    width: 15
  }));

  // Estilo para el encabezado
  ws.getRow(1).font = { bold: true };

  // Agregar filas
  ws.addRows(data);

  // Calcular anchos automáticos
  ws.columns.forEach(column => {
    let maxLength = column.header.length;
    data.forEach(row => {
      const val = row[column.key];
      if (val !== null && val !== undefined) {
        maxLength = Math.max(maxLength, String(val).length);
      }
    });
    // Ajustar ancho (min 10, max 60)
    column.width = Math.min(Math.max(maxLength + 2, 10), 60);
  });

  // Agregar fila de totales
  if (totalColumns && totalColumns.length > 0) {
    const totalRow = {};
    headers.forEach((header, index) => {
      if (index === 0) {
        totalRow[header] = 'TOTAL';
      } else if (totalColumns.includes(header)) {
        const sum = data.reduce((acc, row) => {
          const val = parseFloat(row[header]);
          return acc + (isNaN(val) ? 0 : val);
        }, 0);
        totalRow[header] = parseFloat(sum.toFixed(2));
      } else {
        totalRow[header] = '';
      }
    });

    // Agregar fila
    const addedRow = ws.addRow(totalRow);
    // Negrita para totales
    addedRow.font = { bold: true };
  }
}

/**
 * Exporta Excel completo (Detalle + Resumen)
 */
function exportComplete(logs, options = {}) {
  const {
    filename = 'bitacora_completa',
    getUserName = defaultGetUserName,
    getHours = defaultGetHours,
    getAmount = defaultGetAmount,
    formatDate = defaultFormatDate,
  } = options;

  // Crear workbook
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Bitacora App';
  wb.created = new Date();

  // ========== HOJA 1: DETALLE ==========
  const wsDetail = wb.addWorksheet('Detalle');
  const detailData = prepareDetailData(logs, { getUserName, getHours, getAmount, formatDate });
  addSheetData(wsDetail, detailData, ['Horas', 'Monto $']);

  // ========== HOJA 2: RESUMEN ==========
  const wsSummary = wb.addWorksheet('Resumen');
  const summaryData = prepareSummaryData(logs, { getUserName });
  addSheetData(wsSummary, summaryData, [
    'Total Registros',
    'Facturables',
    'No Facturables',
    'Total Horas',
    'Monto Total $',
    'Monto Facturable $'
  ]);

  // Generar y descargar
  saveWorkbook(wb, filename);
}

/**
 * Prepara los datos para la hoja de detalle
 */
function prepareDetailData(logs, options = {}) {
  const {
    getUserName = defaultGetUserName,
    getHours = defaultGetHours,
    getAmount = defaultGetAmount,
    formatDate = defaultFormatDate,
  } = options;

  return logs.map(log => {
    const startedAt = log.started_at || log.startedAt || log.date;
    const endedAt = log.ended_at || log.endedAt;
    const rate = Number(log.hourly_rate_snapshot || log.hourly_rate || log.rate || 0);
    const hours = getHours(log);
    const amount = getAmount(log);

    // Extraer el texto plano de la nota (remover HTML)
    const noteHtml = log.progress_note || log.note || '';
    const plainNote = htmlToPlain(noteHtml);

    return {
      'ID': log.id || '—',
      'Fecha Inicio': formatDate(startedAt),
      'Fecha Término': endedAt ? formatDate(endedAt) : '—',
      'Proyecto': log.project_name || log.project?.name || '—',
      'Programador': getUserName(log),
      'Actividad': log.activity_code || log.activity || '—',
      'Horas': parseFloat(hours.toFixed(2)),
      'Tarifa $/h': rate,
      'Monto $': amount,
      'Facturable': Number(log.billable) === 1 ? 'Sí' : 'No',
      'Descripción/Avance': plainNote || '—',
    };
  });
}

function htmlToPlain(textOrHtml = '') {
  if (!textOrHtml) return '';
  const div = document.createElement('div');
  div.innerHTML = String(textOrHtml ?? '');
  return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
}

/**
 * Prepara los datos para la hoja de resumen
 */
function prepareSummaryData(logs, options = {}) {
  const {
    getUserName = defaultGetUserName,
  } = options;

  // Agrupar por proyecto y usuario
  const summary = {};

  logs.forEach(log => {
    const projectName = log.project_name || log.project?.name || 'Sin proyecto';
    const userName = getUserName(log);
    const key = `${projectName}|||${userName}`;

    if (!summary[key]) {
      summary[key] = {
        proyecto: projectName,
        programador: userName,
        totalHoras: 0,
        totalMonto: 0,
        registros: 0,
        facturables: 0,
        noFacturables: 0,
        montoFacturable: 0,
      };
    }

    const hours = Number(log.hours ?? log.hours_total ?? 0);
    const amount = Number(log.amount ?? log.total_amount ?? 0);
    const isBillable = Number(log.billable) === 1;

    summary[key].totalHoras += hours;
    summary[key].totalMonto += amount;
    summary[key].registros += 1;

    if (isBillable) {
      summary[key].facturables += 1;
      summary[key].montoFacturable += amount;
    } else {
      summary[key].noFacturables += 1;
    }
  });

  const data = Object.values(summary).map(item => {
    const avgHoursPerEntry = item.totalHoras / item.registros;
    const avgRate = item.totalHoras > 0 ? item.totalMonto / item.totalHoras : 0;

    return {
      'Proyecto': item.proyecto,
      'Programador': item.programador,
      'Total Registros': item.registros,
      'Facturables': item.facturables,
      'No Facturables': item.noFacturables,
      'Total Horas': parseFloat(item.totalHoras.toFixed(2)),
      'Promedio h/Registro': parseFloat(avgHoursPerEntry.toFixed(2)),
      'Tarifa Promedio $/h': parseFloat(avgRate.toFixed(0)),
      'Monto Total $': item.totalMonto,
      'Monto Facturable $': item.montoFacturable,
    };
  });

  // Ordenar por proyecto y luego por programador
  data.sort((a, b) => {
    if (a.Proyecto !== b.Proyecto) {
      return a.Proyecto.localeCompare(b.Proyecto);
    }
    return a.Programador.localeCompare(b.Programador);
  });

  return data;
}

/**
 * Exporta modo detalle
 */
function exportDetail(logs, options = {}) {
  const {
    filename = 'bitacora_detalle',
    getUserName = defaultGetUserName,
    getHours = defaultGetHours,
    getAmount = defaultGetAmount,
    formatDate = defaultFormatDate,
  } = options;

  const data = prepareDetailData(logs, options);

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Registros Detallados');

  addSheetData(ws, data, ['Horas', 'Monto $']);

  saveWorkbook(wb, filename);
}

/**
 * Exporta modo resumen agrupado por proyecto y programador
 */
function exportSummary(logs, options = {}) {
  const {
    filename = 'bitacora_resumen',
    getUserName = defaultGetUserName,
  } = options;

  const data = prepareSummaryData(logs, options);

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Resumen por Proyecto');

  addSheetData(ws, data, ['Total Registros', 'Facturables', 'No Facturables', 'Total Horas', 'Monto Total $', 'Monto Facturable $']);

  saveWorkbook(wb, filename);
}

// ==================== FUNCIONES DEFAULT ====================

function defaultGetUserName(l) {
  return l?.user_name || l?.member_name || l?.username || l?.user?.name || l?.user || '—';
}

function defaultGetHours(l) {
  return Number(l?.hours ?? l?.hours_total ?? 0);
}

function defaultGetAmount(l) {
  return Number(l?.amount ?? l?.total_amount ?? 0);
}

function defaultFormatDate(d) {
  if (!d) return '—';
  try {
    const date = d instanceof Date ? d : new Date(d);
    return date.toLocaleString('es-CL', {
      timeZone: 'America/Santiago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '—';
  }
}
